import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { resolvePack } from "@api/pack";
import { getModuleValue, useWindowSize } from "@app/Helpers";
import BrandLogo from "@components/BrandLogo";
import Button from "@components/Button";
import LinearProgressBar from "@components/LinearGradientProgressBar";
import PagesHeader from "@components/PagesHeader";
import StickyButton from "@components/StickyButton";
import StickyContent from "@components/StickyContent";
import "./style.scss";


function AnswersContent({
  currentQuestion,
  selected,
  multiSelect,
  inputValue,
  rangeValue,
  rangeInputRef,
  handlePickOneClick,
  handleMultiSelectClick,
  handleAnswerClick,
  handleInputChange,
  handleRangeChange,
  setInputValue,
}) {
  const { type, answers } = currentQuestion;

  switch (type) {
    case "select":
      return (
        <div className="qr-poll__answers">
          {answers.map((e, i) => (
            <button
              key={i}
              className={`qr-poll__answer${selected === e.ident ? " qr-poll__answer--selected" : ""}`}
              onClick={() => handlePickOneClick(e.ident)}
            >
              <span className="qr-poll__answer-oval" />
              <p>{e.answer}</p>
            </button>
          ))}
        </div>
      );
    case "selectImg":
      return (
        <div className="qr-poll__answers qr-poll__answers--img">
          {answers.map((e, i) => (
            <button
              key={i}
              className={`qr-poll__answer${selected === e.ident ? " qr-poll__answer--selected" : ""}`}
              onClick={() => handlePickOneClick(e.ident)}
            >
              <img alt="" src={e.answer} className="qr-poll__answer-img" />
            </button>
          ))}
        </div>
      );
    case "select_other":
      return (
        <div className="qr-poll__answers">
          {answers.map((e, i) => (
            <button
              key={i}
              className={`qr-poll__answer${selected === e.ident ? " qr-poll__answer--selected" : ""}`}
              onClick={() => handlePickOneClick(e.ident)}
            >
              <span className="qr-poll__answer-oval" />
              <p>{e.answer}</p>
            </button>
          ))}
          <input
            className="qr-poll__text-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder="Eigene Antwort eingeben"
            disabled={selected !== answers[answers.length - 1].ident}
          />
        </div>
      );
    case "nps":
      return (
        <div className="qr-poll__nps">
          <div className="qr-poll__nps-content">
            {answers.map((e, i) => (
              <button
                key={i}
                className={`qr-poll__answer${selected === e.ident ? " qr-poll__answer--selected" : ""}`}
                onClick={() => handlePickOneClick(e.ident)}
              >
                <p>{i}</p>
              </button>
            ))}
          </div>
          <div className="qr-poll__nps-labels">
            {answers.map((e, i) => (
              <span key={i}>{e.answer}</span>
            ))}
          </div>
        </div>
      );
    case "value":
      return (
        <>
          <div className="qr-poll__range-row">
            <div className="qr-poll__range-wrap">
              <div className="qr-poll__range-track-wrap">
                <div className="qr-poll__range-segments" aria-hidden="true">
                  {answers.map((_, i) => (
                    <span
                      key={i}
                      className={`qr-poll__range-segment${i <= parseInt(rangeValue, 10) ? " qr-poll__range-segment--active" : ""}`}
                    />
                  ))}
                </div>
                <input
                  className="qr-poll__range-input"
                  ref={rangeInputRef}
                  type="range"
                  min="0"
                  max={answers.length - 1}
                  value={rangeValue}
                  onChange={handleRangeChange}
                />
              </div>
            </div>
            <div className="qr-poll__range-display" aria-live="polite">
              <span className="qr-poll__range-num">{parseInt(rangeValue, 10) + 1}</span>
              <svg className="qr-poll__range-star" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path fillRule="evenodd" clipRule="evenodd" d="M14.0229 9.78254L12 5.51261L9.97706 9.78254L5.45362 10.4673L8.72681 13.7909L7.95411 18.484L12 16.2682L16.0459 18.484L15.2732 13.7909L18.5464 10.4673L14.0229 9.78254ZM9.01281 8.4L5.24482 8.97036C4.05383 9.15064 3.57828 10.6753 4.44008 11.5504L7.16663 14.319L6.52298 18.2283C6.31954 19.464 7.56455 20.4063 8.6298 19.8229L12 17.9772L15.3702 19.8229C16.4354 20.4063 17.6805 19.464 17.477 18.2283L16.8334 14.319L19.5599 11.5504C20.4217 10.6753 19.9462 9.15064 18.7552 8.97036L14.9872 8.4L13.3021 4.84318C12.7695 3.71894 11.2305 3.71894 10.6979 4.84318L9.01281 8.4Z" fill="white" />
              </svg>
            </div>
          </div>
          <div className="qr-poll__range-labels">
            <span>{answers[0].answer}</span>
            <span>{answers[answers.length - 1].answer}</span>
          </div>
        </>
      );
    case "order":
      return (
        <div className="qr-poll__answers">
          {answers.map(({ answer }, i) => (
            <button
              key={i}
              className="qr-poll__answer"
              onClick={() => handleAnswerClick(i)}
            >
              <input
                type="text"
                className="qr-poll__order-input"
                maxLength="1"
                onChange={e => handleInputChange(e, i)}
              />
              <p>{answer}</p>
            </button>
          ))}
        </div>
      );
    case "multiselect":
    case "multiselect_limit":
      return (
        <div className="qr-poll__answers">
          {answers.map((e, i) => (
            <button
              key={i}
              className={`qr-poll__answer${multiSelect.includes(e.ident) ? " qr-poll__answer--selected" : ""}`}
              onClick={() => handleMultiSelectClick(e.ident)}
            >
              <span className="qr-poll__answer-checkbox">
                {multiSelect.includes(e.ident) && <Check size={10} strokeWidth={3} />}
              </span>
              <p>{e.answer}</p>
            </button>
          ))}
        </div>
      );
    case "multiselect_other":
    case "multiselect_limit_other":
      return (
        <div className="qr-poll__answers">
          {answers.map((e, i) => (
            <button
              key={i}
              className={`qr-poll__answer${multiSelect.includes(e.ident) ? " qr-poll__answer--selected" : ""}`}
              onClick={() => handleMultiSelectClick(e.ident)}
            >
              <span className="qr-poll__answer-checkbox">
                {multiSelect.includes(e.ident) && <Check size={10} strokeWidth={3} />}
              </span>
              <p>{e.answer}</p>
            </button>
          ))}
          <input
            className="qr-poll__text-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder="Eigene Antwort eingeben"
            disabled={!multiSelect.includes(answers[answers.length - 1].ident)}
          />
        </div>
      );
    case "input_open":
      return (
        <div className="qr-poll__answers">
          <input
            className="qr-poll__text-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            placeholder="Eigene Antwort eingeben"
          />
        </div>
      );
    case "select_explain":
      return (
        <div className="qr-poll__answers">
          {answers.map((e, i) => (
            <React.Fragment key={i}>
              <button
                className={`qr-poll__answer${selected === e.ident ? " qr-poll__answer--selected" : ""}`}
                onClick={() => handlePickOneClick(e.ident)}
              >
                <span className="qr-poll__answer-oval" />
                <p>{e.answer}</p>
              </button>
              {selected === e.ident && (
                <input
                  className="qr-poll__text-input"
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  placeholder="Eigene Antwort eingeben"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function QrPoll({ packUuid, activation, onComplete }) {
  const pollModule = activation?.content?.modules?.find(m => m.type === "poll");
  const pollQuestions = pollModule?.metadata?.questions ?? null;
  const heroImageUrl = getModuleValue(activation?.content?.modules, "hero_image", "file.url") ?? null;

  const { width } = useWindowSize();
  const XGA = width >= 1020;

  const [sending, setSending] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [currentNode, setCurrentNode] = useState(() =>
    pollQuestions ? Object.keys(pollQuestions)[0] : null
  );
  const [nextNodeObject, setNextNodeObject] = useState(null);
  const [rangeValue, setRangeValue] = useState(0);
  const [selected, setSelected] = useState(null);
  const [multiSelect, setMultiSelect] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [userInputs, setUserInputs] = useState([]);
  const rangeInputRef = useRef(null);
  const submittingRef = useRef(false);

  const currentQuestion = pollQuestions && currentNode ? pollQuestions[currentNode] : null;

  useEffect(() => {
    if (!currentQuestion?.adjacentTo) return;
    if (selected !== null) {
      const nextNode = currentQuestion.adjacentTo.find(e =>
        Array.isArray(e.value) ? e.value.includes(selected) : e.value === selected
      );
      setNextNodeObject(nextNode || null);
    } else {
      setNextNodeObject(null);
    }
  }, [selected, currentQuestion]);

  useEffect(() => {
    if (!currentQuestion?.adjacentTo || multiSelect.length === 0) return;
    const nextNode = currentQuestion.adjacentTo.find(
      e => e.value === multiSelect[multiSelect.length - 1]
    );
    setNextNodeObject(nextNode || null);
  }, [multiSelect, currentQuestion]);

  useEffect(() => {
    if (!currentQuestion?.adjacentTo || inputValue === "") return;
    const nextNode = currentQuestion.adjacentTo.find(e => e?.value);
    setNextNodeObject(nextNode || null);
  }, [inputValue, currentQuestion]);


  if (!pollQuestions || !currentNode || !currentQuestion) return null;

  const pollQuestionKeys = Object.keys(pollQuestions);
  const currentIndex = pollQuestionKeys.indexOf(currentNode);
  const progress = 0.1 + ((currentIndex + 1) / (pollQuestionKeys.length + 1)) * 0.9;

  function handlePickOneClick(ident) {
    setSelected(ident);
    setInputValue("");
  }

  const handleRangeChange = (e) => {
    const index = e.target.value;
    setSelected(currentQuestion.answers[index].ident);
    setRangeValue(index);
  };

  function handleMultiSelectClick(ident) {
    let updated = [...multiSelect];
    const { multiselectLimit } = currentQuestion;
    if (updated.includes(ident)) {
      setMultiSelect(updated.filter(e => e !== ident));
      return;
    }
    if (multiselectLimit && updated.length >= multiselectLimit) {
      updated.shift();
    }
    updated.push(ident);
    setMultiSelect(updated);
  }

  function handleAnswerClick(index) {
    setSelected(index);
  }

  function handleInputChange(e, index) {
    const val = e.target.value;
    if (!isNaN(val) && val !== "") {
      const alpha = String.fromCharCode(96 + parseInt(val, 10));
      setUserInputs(prev => {
        const next = [...prev];
        next[index] = alpha;
        return next;
      });
    }
  }

  function buttonDisabler() {
    if (sending || submittingRef.current) return true;
    const { type, answers } = currentQuestion;
    switch (type) {
      case "select":
      case "selectImg":
      case "nps":
      case "value":
        return selected === null;
      case "select_other": {
        const isOther = selected === answers[answers.length - 1].ident;
        if (!isOther) return selected === null;
        return !(inputValue.length > 0 && selected !== null);
      }
      case "order": {
        if (userInputs.length < answers.length) return true;
        const seen = new Set();
        for (let i = 0; i < answers.length; i++) {
          const val = userInputs[i];
          if (!val || seen.has(val)) return true;
          seen.add(val);
        }
        const nums = Array.from(seen).map(v => v.charCodeAt(0) - 96).sort((a, b) => a - b);
        for (let i = 0; i < nums.length; i++) {
          if (nums[i] !== i + 1) return true;
        }
        return false;
      }
      case "multiselect":
      case "multiselect_limit":
        return multiSelect.length === 0;
      case "multiselect_other":
      case "multiselect_limit_other": {
        if (multiSelect.length === 0) return true;
        const isOther = multiSelect.includes(answers[answers.length - 1].ident);
        return isOther ? inputValue.length === 0 : false;
      }
      case "input_open":
        return inputValue.length === 0;
      case "select_explain":
        return !(inputValue.length > 0 && selected !== null);
      default:
        return true;
    }
  }

  function handleNextClick() {
    const { type } = currentQuestion;
    let answer_ident;

    switch (type) {
      case "order":
        answer_ident = userInputs;
        break;
      case "multiselect":
      case "multiselect_limit":
      case "multiselect_other":
      case "multiselect_limit_other":
        answer_ident = multiSelect;
        break;
      case "input_open":
        answer_ident = inputValue;
        break;
      default:
        answer_ident = selected;
    }

    const newAnswers = [
      ...answers,
      { question_ident: currentQuestion.ident, answer_ident },
    ];

    if (
      ["select_other", "multiselect_other", "multiselect_limit_other", "select_explain"].includes(type) &&
      inputValue.length > 0
    ) {
      newAnswers.push({
        question_ident: `${currentQuestion.ident}_input`,
        answer_ident: inputValue,
      });
    }

    setAnswers(newAnswers);

    let resolvedNext = nextNodeObject;
    if (!resolvedNext && currentQuestion.adjacentTo) {
      resolvedNext = currentQuestion.adjacentTo.find(e => e.value === "*") || null;
    }

    if (!resolvedNext) {
      submittingRef.current = true;
      setSending(true);
      resolvePack({
        uuid: packUuid,
        answers: newAnswers,
        successCallback: async (res) => {
          try {
            await onComplete(res?.data);
          } catch {
            submittingRef.current = false;
            setSending(false);
          }
        },
        errorCallback: () => { submittingRef.current = false; setSending(false); },
      });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentNode(resolvedNext.node);
    setSelected(null);
    setMultiSelect([]);
    setInputValue("");
    setRangeValue(0);
    setUserInputs([]);
    setNextNodeObject(null);
    if (rangeInputRef.current) {
      rangeInputRef.current.value = 0;
    }
  }

  const isLastQuestion = !nextNodeObject && currentQuestion.adjacentTo === null;
  const brands = activation?.brands ?? [];
  const buttonLabel = isLastQuestion ? "Abschicken" : "Weiter";

  return (
    <section className="qr-poll">
      <div>
        <PagesHeader>
          <p><strong>Vuse</strong> Umfrage</p>
        </PagesHeader>
        <div className="qr-poll__body">
          <div className="qr-poll__left">
            <LinearProgressBar value={progress} startStepColor="#47B5BF" endStepColor="#008888" />
            <div className="qr-poll__question-wrap">
              <h2 className="qr-poll__question">{currentQuestion.question}</h2>
            </div>
            <div className="qr-poll__card">
              <AnswersContent
                currentQuestion={currentQuestion}
                selected={selected}
                multiSelect={multiSelect}
                inputValue={inputValue}
                rangeValue={rangeValue}
                rangeInputRef={rangeInputRef}
                handlePickOneClick={handlePickOneClick}
                handleMultiSelectClick={handleMultiSelectClick}
                handleAnswerClick={handleAnswerClick}
                handleInputChange={handleInputChange}
                handleRangeChange={handleRangeChange}
                setInputValue={setInputValue}
              />
            </div>
          </div>
          <div className="qr-poll__side">
            {brands.length > 0 && <BrandLogo brands={brands} />}
            {heroImageUrl && (
              <div className="qr-poll__img-wrapper">
                <img src={heroImageUrl} alt="" />
              </div>
            )}
            {XGA && (
              <StickyContent>
                <div className="qr-poll__desktop-buttons">
                  <Button
                    action={handleNextClick}
                    disabled={buttonDisabler()}
                    btnClass="ultra-insider"
                  >
                    {buttonLabel}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>
      {!XGA && (
        <StickyButton
          action={handleNextClick}
          disabled={buttonDisabler()}
        >
          {buttonLabel}
        </StickyButton>
      )}
    </section>
  );
}
